import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye } from "@phosphor-icons/react";
import { Ghost } from "@phosphor-icons/react";

const BAND_STYLE = {
  converted:       { label: "Converted",        klass: "bg-emerald-100 text-emerald-800" },
  ghosted:         { label: "Ghosted",          klass: "bg-rose-100 text-rose-800" },
  engaged:         { label: "Engaged",          klass: "bg-indigo-100 text-indigo-800" },
  low_engagement:  { label: "Low Engagement",   klass: "bg-slate-100 text-slate-700" },
};

export default function PublicPdfEngagementReport({ user, logout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState("all");

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/public-pdf-engagement?requester_role=${user?.role || ""}&limit=200`);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, [user?.role]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = (data?.rows || []).filter(r => bandFilter === "all" || r.engagement_band === bandFilter);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="px-8 pt-8 pb-6 max-w-6xl">
          <Link to="/reports-hub" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-hub">
            <ArrowLeft size={14} /> Reports Hub
          </Link>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-testid="public-pdf-title">
            Public-PDF Engagement vs Conversion
          </h1>
          <p className="text-slate-600 text-sm mt-1">PDF view counts cross-referenced with quote→SO conversion. Spot ghosted prospects for follow-up.</p>
        </div>

        <div className="px-8 pb-12 max-w-6xl space-y-6">
          {loading ? (
            <div className="text-center text-slate-400 py-16">Loading…</div>
          ) : data?.error ? (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-6">{data.error}</div>
          ) : (
            <>
              {/* KPI tiles also act as filters */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="engagement-kpis">
                {[
                  { k: "all", label: "Total Tracked Docs", val: data?.total_tracked_docs, klass: "bg-white border-slate-200" },
                  { k: "all", label: "Total Views", val: data?.total_access_events, klass: "bg-white border-slate-200" },
                  { k: "converted", label: "Converted", val: data?.converted_count, klass: "bg-emerald-50 border-emerald-200" },
                  { k: "ghosted", label: "Ghosted", val: data?.ghosted_count, klass: "bg-rose-50 border-rose-200" },
                  { k: "all", label: "Conv. Rate", val: `${data?.conversion_rate_pct ?? 0}%`, klass: "bg-indigo-50 border-indigo-200" },
                ].map((tile, i) => (
                  <button key={i} onClick={() => setBandFilter(tile.k)}
                          data-testid={`kpi-${i}`}
                          className={`text-left border rounded-xl p-4 transition-all ${tile.klass} ${bandFilter === tile.k && tile.k !== "all" ? "ring-2 ring-offset-2 ring-slate-400" : ""}`}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{tile.label}</div>
                    <div className="text-3xl font-black text-slate-900 mt-1">{tile.val ?? 0}</div>
                  </button>
                ))}
              </div>

              {/* Filter row */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Filter:</span>
                {["all", "converted", "ghosted", "engaged", "low_engagement"].map(b => (
                  <button key={b} onClick={() => setBandFilter(b)}
                          data-testid={`band-filter-${b}`}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${bandFilter === b ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                    {b === "all" ? "All" : BAND_STYLE[b]?.label}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="engagement-table">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Doc</th>
                      <th className="px-5 py-3 font-semibold">Customer</th>
                      <th className="px-5 py-3 font-semibold text-right">Total</th>
                      <th className="px-5 py-3 font-semibold text-right">Views</th>
                      <th className="px-5 py-3 font-semibold">Last Access</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Engagement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No tracked docs in this filter.</td></tr>
                    ) : rows.map(r => {
                      const band = BAND_STYLE[r.engagement_band] || BAND_STYLE.low_engagement;
                      return (
                        <tr key={r.doc_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pdf-row-${r.doc_id}`}>
                          <td className="px-5 py-3">
                            <div className="font-semibold text-slate-900">{r.number}</div>
                            <div className="text-xs text-slate-500 capitalize">{r.doc_type}</div>
                          </td>
                          <td className="px-5 py-3 text-slate-700">{r.customer_name || "—"}</td>
                          <td className="px-5 py-3 text-right font-mono text-sm">₹{Number(r.total || 0).toLocaleString("en-IN")}</td>
                          <td className="px-5 py-3 text-right">
                            <span className="inline-flex items-center gap-1 text-slate-700 font-bold">
                              <Eye size={13} weight="bold" /> {r.access_count}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-500">
                            {r.last_access ? new Date(r.last_access).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—"}
                          </td>
                          <td className="px-5 py-3 text-xs">
                            {r.converted_to_so ? (
                              <span className="text-emerald-700 font-semibold">SO {r.linked_so_number}</span>
                            ) : (
                              <span className="text-slate-500 capitalize">{r.status?.replace(/_/g, " ")}</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${band.klass}`}>
                              {band.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-3">
                <Ghost size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold mb-1">Ghosted definition</div>
                  Customers who opened the public quote ≥3 times but never converted to a Sales Order. These are warm prospects ripe for a follow-up call or WhatsApp nudge.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
